import React from 'react';

import { FlatTableCell, FlatTableRow, FlatTableRowHeader } from 'carbon-react/lib/components/flat-table';

import { DragHandle, useTreeRow } from '@futrli/ui-components';

import { DriverDragHandlers } from '../../hooks/useDriversDragDrop/useDriversDragDrop';
import { DriverGroupRowActions } from './driver-group-row-actions';
import { DriverRow } from './driver-row';
import { DriverRowName } from './driver-row-name';
import { formatDriverValue } from './utils';
import { NonFinancialDriverTreeNode } from '../../types';

interface Props {
  row: NonFinancialDriverTreeNode;
  columns: string[];
  isNested?: boolean;
  isReordering?: boolean;
  isUpdating?: boolean;
  drag: DriverDragHandlers;
  index: number;
  flatData?: NonFinancialDriverTreeNode[];
}

const renderSubRows = (
  children: NonFinancialDriverTreeNode[],
  columns: string[],
  isReordering: boolean,
  isUpdating: boolean,
  drag: DriverDragHandlers,
  flatData?: NonFinancialDriverTreeNode[]
): React.ReactNode[] =>
  children.map(child => {
    const childIndex = flatData?.findIndex(n => n.id === child.id) || 0;
    if (child.isGroup) {
      return (
        <DriverGroupRow
          key={child.id}
          row={child}
          columns={columns}
          isNested
          isReordering={isReordering}
          isUpdating={isUpdating}
          drag={drag}
          index={childIndex}
          flatData={flatData}
        />
      );
    }
    return (
      <DriverRow
        key={child.id}
        row={child}
        columns={columns}
        isNested
        isReordering={isReordering}
        isUpdating={isUpdating}
        drag={drag}
        index={childIndex}
      />
    );
  });

export const DriverGroupRow = ({ row, columns, isNested, isReordering, isUpdating, drag, index, flatData }: Props) => {
  const { rowRef, indentPx } = useTreeRow({
    depth: row.depth || 0,
    drag,
    index,
    isReordering: !!isReordering,
    type: row.type
  });

  const subRows =
    row.children && row.children.length > 0
      ? renderSubRows(row.children, columns, !!isReordering, !!isUpdating, drag, flatData)
      : undefined;

  return (
    <FlatTableRow
      expandable={!isReordering}
      expanded
      subRows={subRows}
      bgColor={isNested ? '#F4F5F6' : undefined}
      key={row.id}
      ref={rowRef}
      data-element={isUpdating ? 'updating-driver-row' : undefined}
    >
      {isReordering ? (
        <FlatTableRowHeader width={300} pl={`${indentPx}px`}>
          <div style={{ display: 'flex', alignItems: 'center', height: '20px' }}>
            <DragHandle drag={drag} index={index} rowRef={rowRef} useFirstColumnDragImage />
            <DriverRowName
              name={row.name}
              isExcelDriver={row.is_excel_driver}
              isGroup
              withDragOffset
            />
          </div>
        </FlatTableRowHeader>
      ) : (
        <FlatTableRowHeader width={300} pl={`${indentPx}px`}>
          <DriverRowName name={row.name} isExcelDriver={row.is_excel_driver} isGroup />
        </FlatTableRowHeader>
      )}
      {columns.map(column => (
        <FlatTableCell key={column} width={120}>
          {formatDriverValue(row.values[column], row.decimalDisplay)}
        </FlatTableCell>
      ))}
      <FlatTableCell width={150}>
        <DriverGroupRowActions row={row} drag={drag} index={index} />
      </FlatTableCell>
    </FlatTableRow>
  );
};
